import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from "react";
import api from "../lib/api";
import toast from "react-hot-toast";

interface Ticket {
  id: string;
  ticketNumber: number;
  title: string;
  description: string;
  status: "OPEN" | "IN_PROGRESS" | "RESOLVED" | "CLOSED";
  priority: "LOW" | "MEDIUM" | "HIGH" | "URGENT";
  category?: "SOFTWARE" | "HARDWARE" | "RED" | "ERP" | "OTRO" | null;
  isRead: boolean;
  requesterId: string;
  assigneeId?: string;
  dueAt?: string | null;
  closedAt?: string;
  // Marca de métricas (ver lib/metricsExclusion).
  metricsExclusion?: "TEST" | "SLA" | null;
  metricsExclusionReason?: string | null;
  createdAt: string;
  updatedAt: string;
  requester: {
    id: string;
    name: string;
    email: string;
  };
  assignee?: {
    id: string;
    name: string;
    email: string;
  };
}

interface TicketFilters {
  q?: string;
  status?: string;
  priority?: string;
  category?: string;
  requesterId?: string;
  assigneeId?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: string;
  // Triage filters (solo aplican para AGENT/ADMIN)
  filter?: "fresh" | "unassigned" | "unread" | "mine" | "overdue" | "";
  // Tickets de prueba: solo ADMIN (para otros roles el back lo ignora).
  test?: "include" | "only";
}

interface FetchParams {
  filters: TicketFilters;
  page: number;
  pageSize: number;
}

/** Por qué no se pudo abrir un ticket (404 si lo borraron, 403 si no hay acceso). */
export interface TicketLoadError {
  status?: number;
  code?: string;
  message: string;
}

interface GetTicketOptions {
  /** Si se pasa, el error se le entrega a quien llama y no se muestra el toast. */
  onError?: (error: TicketLoadError) => void;
}

type TicketStatus = Ticket["status"];

interface TicketsContextValue {
  tickets: Ticket[];
  isLoading: boolean;
  /** Mensaje del último pedido de la lista si falló; null si salió bien. */
  error: string | null;
  total: number;
  /** Totales por estado con los filtros actuales, sin la pestaña (back). */
  countsByStatus: Record<TicketStatus, number> | null;
  fetchTickets: (params: FetchParams) => Promise<void>;
  createTicket: (ticketData: {
    title: string;
    description: string;
    priority: string;
    category?: string;
  }) => Promise<Ticket>;
  updateTicket: (id: string, updates: Partial<Ticket>) => Promise<Ticket>;
  deleteTicket: (id: string) => Promise<void>;
  getTicketById: (
    id: string,
    options?: GetTicketOptions,
  ) => Promise<Ticket | null>;
  addComment: (ticketId: string, message: string) => Promise<any>;
}

const TicketsContext = createContext<TicketsContextValue | null>(null);

export const TicketsProvider = ({ children }: { children: ReactNode }) => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [countsByStatus, setCountsByStatus] = useState<Record<
    TicketStatus,
    number
  > | null>(null);
  // Número del último pedido: una respuesta más lenta de un pedido viejo no
  // pisa a la del último (p. ej. al tipear en la búsqueda).
  const lastRequest = useRef(0);

  const fetchTickets = useCallback(
    async ({ filters, page, pageSize }: FetchParams) => {
      const requestId = ++lastRequest.current;
      try {
        setIsLoading(true);
        const params = new URLSearchParams();

        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== "") {
            params.append(key, String(value));
          }
        });

        params.append("page", String(page));
        params.append("pageSize", String(pageSize));

        const response = await api.get(`/api/tickets?${params.toString()}`);
        if (requestId !== lastRequest.current) return;
        const payload = response.data?.data || {};
        const list: Ticket[] = payload.data || [];
        const pagination = payload.pagination || {};

        setTickets(Array.isArray(list) ? list : []);
        setTotal(typeof pagination.total === "number" ? pagination.total : 0);
        setCountsByStatus(payload.countsByStatus ?? null);
        setError(null);
      } catch (error: any) {
        if (requestId !== lastRequest.current) return;
        setError(
          error.response?.data?.error?.message || "Error al cargar tickets",
        );
      } finally {
        if (requestId === lastRequest.current) setIsLoading(false);
      }
    },
    [],
  );

  const createTicket = useCallback(
    async (ticketData: {
      title: string;
      description: string;
      priority: string;
      category?: string;
    }) => {
      try {
        setIsLoading(true);
        const response = await api.post("/api/tickets", ticketData);
        const newTicket = response.data.data;

        setTickets((prev) => [newTicket, ...prev]);
        setTotal((prev) => prev + 1);

        toast.success("Ticket creado exitosamente");
        return newTicket;
      } catch (error: any) {
        const message =
          error.response?.data?.error?.message || "Error al crear ticket";
        toast.error(message);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const updateTicket = useCallback(
    async (id: string, updates: Partial<Ticket>) => {
      try {
        setIsLoading(true);
        const response = await api.patch(`/api/tickets/${id}`, updates);
        const updatedTicket = response.data.data;

        setTickets((prev) =>
          prev.map((ticket) => (ticket.id === id ? updatedTicket : ticket)),
        );

        toast.success("Ticket actualizado exitosamente");
        return updatedTicket;
      } catch (error: any) {
        const message =
          error.response?.data?.error?.message || "Error al actualizar ticket";
        toast.error(message);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const deleteTicket = useCallback(async (id: string) => {
    try {
      setIsLoading(true);
      await api.delete(`/api/tickets/${id}`);

      setTickets((prev) => prev.filter((ticket) => ticket.id !== id));
      setTotal((prev) => prev - 1);

      toast.success("Ticket eliminado exitosamente");
    } catch (error: any) {
      const message =
        error.response?.data?.error?.message || "Error al eliminar ticket";
      toast.error(message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getTicketById = useCallback(
    async (id: string, options?: GetTicketOptions): Promise<Ticket | null> => {
      try {
        const response = await api.get(`/api/tickets/${id}`);
        const ticket = response.data.data;
        if (ticket) {
          // Marcar como leído en el state compartido (si está en la lista
          // actual). Antes lo hacíamos via window.dispatchEvent + listener
          // en otras instancias del hook; ahora la lista es única.
          setTickets((prev) =>
            prev.map((t) => (t.id === id ? { ...t, isRead: true } : t)),
          );
        }
        return ticket;
      } catch (error: any) {
        const message =
          error.response?.data?.error?.message || "Error al cargar ticket";
        if (options?.onError) {
          options.onError({
            status: error.response?.status,
            code: error.response?.data?.error?.code,
            message,
          });
        } else {
          toast.error(message);
        }
        return null;
      }
    },
    [],
  );

  const addComment = useCallback(async (ticketId: string, message: string) => {
    try {
      const response = await api.post(`/api/tickets/${ticketId}/comments`, {
        message,
      });
      const newComment = response.data.data;
      setTickets((prev) =>
        prev.map((t) => {
          if (t.id !== ticketId) return t;
          const comments = (t as any).comments
            ? [...(t as any).comments, newComment]
            : [newComment];
          return { ...(t as any), comments } as any;
        }),
      );
      return newComment;
    } catch (error: any) {
      const errMessage =
        error.response?.data?.error?.message || "Error al agregar comentario";
      toast.error(errMessage);
      throw error;
    }
  }, []);

  const value: TicketsContextValue = {
    tickets,
    isLoading,
    error,
    total,
    countsByStatus,
    fetchTickets,
    createTicket,
    updateTicket,
    deleteTicket,
    getTicketById,
    addComment,
  };

  return (
    <TicketsContext.Provider value={value}>{children}</TicketsContext.Provider>
  );
};

export const useTickets = (): TicketsContextValue => {
  const ctx = useContext(TicketsContext);
  if (!ctx) {
    throw new Error("useTickets must be used within a TicketsProvider");
  }
  return ctx;
};
